import {
  ORDER_FEATURES,
  OrderFeature,
  OrderFeatureDefinition,
  schFireplaceTypes,
  schPorchPatioDeckSubtypes,
  schSolarPanelOwnershipTypes,
} from '@aloft/toolkit-shared';
import { toOpts } from '@aloft/toolkit-sui';

import {
  getAloftAreaJSONPropertyGridField,
  getAloftPriceJSONPropertyGridField,
  getCraftsmanQualityPropertyGridField,
  getNumberPropertyGridField,
} from '../../components/PropertyGrid/fields/utils.tsx';
import { PropertyGridField } from '../../components/PropertyGrid/types.ts';

type FeatureGridOrderFeatures = {
  [feature in OrderFeature]: OrderFeatureDefinition & {
    propertyGridFields: PropertyGridField[];
  };
};

export const FEATURE_GRID_ORDER_FEATURES: FeatureGridOrderFeatures = {
  adu: {
    ...ORDER_FEATURES.adu,
    propertyGridFields: [
      getAloftAreaJSONPropertyGridField({ fieldName: 'editableData.improvements.adu.area' }),
      getCraftsmanQualityPropertyGridField({ fieldName: 'editableData.improvements.adu.quality' }),
      getNumberPropertyGridField({
        fieldName: 'editableData.improvements.adu.fullBaths',
        label: 'Baths, Full',
      }),
      getNumberPropertyGridField({
        fieldName: 'editableData.improvements.adu.halfBaths',
        label: 'Baths, Half',
      }),
    ],
  },
  attic: {
    ...ORDER_FEATURES.attic,
    propertyGridFields: [
      getAloftAreaJSONPropertyGridField({ fieldName: 'editableData.improvements.attic.area' }),
      getCraftsmanQualityPropertyGridField({
        fieldName: 'editableData.improvements.attic.quality',
      }),
    ],
  },
  barn: {
    ...ORDER_FEATURES.barn,
    propertyGridFields: [
      getAloftAreaJSONPropertyGridField({ fieldName: 'editableData.improvements.barn.area' }),
      getCraftsmanQualityPropertyGridField({ fieldName: 'editableData.improvements.barn.quality' }),
    ],
  },
  basement: {
    ...ORDER_FEATURES.basement,
    propertyGridFields: [
      getAloftAreaJSONPropertyGridField({
        fieldName: 'editableData.improvements.basement.totalArea',
        label: 'Total Area',
      }),
      getAloftAreaJSONPropertyGridField({
        fieldName: 'editableData.improvements.basement.finishedArea',
        label: 'Finished Area',
      }),
      getNumberPropertyGridField({
        fieldName: 'editableData.improvements.basement.fullBaths',
        label: 'Baths, Full',
      }),
      getNumberPropertyGridField({
        fieldName: 'editableData.improvements.basement.halfBaths',
        label: 'Baths, Half',
      }),
    ],
  },
  carport: {
    ...ORDER_FEATURES.carport,
    propertyGridFields: [
      getAloftAreaJSONPropertyGridField({ fieldName: 'editableData.improvements.carport.area' }),
      getNumberPropertyGridField({
        fieldName: 'editableData.improvements.carport.spaces',
        label: 'Spaces',
      }),
      getCraftsmanQualityPropertyGridField({
        fieldName: 'editableData.improvements.carport.quality',
      }),
    ],
  },
  deck: {
    ...ORDER_FEATURES.deck,
    propertyGridFields: [
      {
        fieldName: 'editableData.improvements.deck.type',
        label: 'Type',
        options: toOpts(schPorchPatioDeckSubtypes.options),
      },
      getAloftAreaJSONPropertyGridField({ fieldName: 'editableData.improvements.deck.area' }),
      getCraftsmanQualityPropertyGridField({ fieldName: 'editableData.improvements.deck.quality' }),
    ],
  },
  fireplace: {
    ...ORDER_FEATURES.fireplace,
    propertyGridFields: [
      {
        fieldName: 'editableData.improvements.fireplace.type',
        label: 'Type',
        options: toOpts(schFireplaceTypes.options),
      },
      getNumberPropertyGridField({
        fieldName: 'editableData.improvements.fireplace.count',
        label: 'Count',
      }),
    ],
  },
  garage: {
    ...ORDER_FEATURES.garage,
    propertyGridFields: [
      getAloftAreaJSONPropertyGridField({ fieldName: 'editableData.improvements.garage.area' }),
      getNumberPropertyGridField({
        fieldName: 'editableData.improvements.garage.spaces',
        label: 'Spaces',
      }),
      getCraftsmanQualityPropertyGridField({
        fieldName: 'editableData.improvements.garage.quality',
      }),
    ],
  },
  detachedGarage: {
    ...ORDER_FEATURES.detachedGarage,
    propertyGridFields: [
      getAloftAreaJSONPropertyGridField({
        fieldName: 'editableData.improvements.detachedGarage.area',
      }),
      getNumberPropertyGridField({
        fieldName: 'editableData.improvements.detachedGarage.spaces',
        label: 'Spaces',
      }),
      getCraftsmanQualityPropertyGridField({
        fieldName: 'editableData.improvements.detachedGarage.quality',
      }),
    ],
  },
  manualCost: {
    ...ORDER_FEATURES.manualCost,
    propertyGridFields: [
      {
        fieldName: 'editableData.improvements.manualCostDisplayName',
        label: 'Display Name',
      },
      getAloftPriceJSONPropertyGridField({ fieldName: 'editableData.improvements.manualCost' }),
    ],
  },
  other: {
    ...ORDER_FEATURES.other,
    propertyGridFields: [
      {
        fieldName: 'editableData.improvements.otherAdjustmentsDisplayName',
        label: 'Display Name',
      },
    ],
  },
  patio: {
    ...ORDER_FEATURES.patio,
    propertyGridFields: [
      {
        fieldName: 'editableData.improvements.patio.type',
        label: 'Type',
        options: toOpts(schPorchPatioDeckSubtypes.options),
      },
      getAloftAreaJSONPropertyGridField({ fieldName: 'editableData.improvements.patio.area' }),
      getCraftsmanQualityPropertyGridField({
        fieldName: 'editableData.improvements.patio.quality',
      }),
    ],
  },
  pool: {
    ...ORDER_FEATURES.pool,
    propertyGridFields: [
      getAloftAreaJSONPropertyGridField({ fieldName: 'editableData.improvements.pool.area' }),
      getCraftsmanQualityPropertyGridField({ fieldName: 'editableData.improvements.pool.quality' }),
    ],
  },
  porch: {
    ...ORDER_FEATURES.porch,
    propertyGridFields: [
      {
        fieldName: 'editableData.improvements.porch.type',
        label: 'Type',
        options: toOpts(schPorchPatioDeckSubtypes.options),
      },
      getAloftAreaJSONPropertyGridField({ fieldName: 'editableData.improvements.porch.area' }),
      getCraftsmanQualityPropertyGridField({
        fieldName: 'editableData.improvements.porch.quality',
      }),
    ],
  },
  shed: {
    ...ORDER_FEATURES.shed,
    propertyGridFields: [
      getAloftAreaJSONPropertyGridField({ fieldName: 'editableData.improvements.shed.area' }),
      getCraftsmanQualityPropertyGridField({ fieldName: 'editableData.improvements.shed.quality' }),
    ],
  },
  solarPanel: {
    ...ORDER_FEATURES.solarPanel,
    propertyGridFields: [
      {
        fieldName: 'editableData.improvements.solarPanel.ownership',
        label: 'Ownership',
        options: toOpts(schSolarPanelOwnershipTypes.options),
      },
    ],
  },
  spa: {
    ...ORDER_FEATURES.spa,
    propertyGridFields: [
      getAloftAreaJSONPropertyGridField({ fieldName: 'editableData.improvements.spa.area' }),
      getCraftsmanQualityPropertyGridField({ fieldName: 'editableData.improvements.spa.quality' }),
    ],
  },
  workshop: {
    ...ORDER_FEATURES.workshop,
    propertyGridFields: [
      getAloftAreaJSONPropertyGridField({ fieldName: 'editableData.improvements.workshop.area' }),
      getCraftsmanQualityPropertyGridField({
        fieldName: 'editableData.improvements.workshop.quality',
      }),
    ],
  },
};
